import React, { useMemo, useState } from 'react'
import { Link, usePage } from '@inertiajs/inertia-react'
import { Inertia } from '@inertiajs/inertia'
import Layout from '../../Shared/Layout/LayoutAdmin'
import LayoutSiaLearner from '../../Shared/Layout/LayoutSiaLearner'
import Breadcrumbs from '../../Shared/Components/Breadcrumbs'
import Row from 'react-bootstrap/Row'
import Col from 'react-bootstrap/Col'
import Card from 'react-bootstrap/Card'
import Nav from 'react-bootstrap/Nav'
import { getYouTubeEmbedSrc } from '../../utils/youtube'
import { documentKindLabel, isSpreadsheet } from '../../utils/siaFile'
import { SIA_NAVY, SIA_GOLD_ACTIVE_BG, SIA_GOLD_ACTIVE_TEXT } from '../../constants/siaTheme'

const TAB_ORDER = ['producto', 'llenado', 'condiciones', 'recursos', 'videos_adicionales']

const TAB_LABELS = {
    producto: 'Producto',
    llenado: 'Llenado',
    condiciones: 'Condiciones',
    recursos: 'Recursos',
    videos_adicionales: 'Videos adicionales',
}

const seenThisSession = new Set()

const mediaListStyle = { maxHeight: '70vh', overflowY: 'auto', minHeight: 0 }

function reportProgress(item, payload) {
    if (!item?.id || item.locked || item.completed || seenThisSession.has(item.id)) return
    window.axios
        .post(route('sia.item.progress', item.id), payload)
        .then(({ data }) => {
            if (data?.completed) {
                seenThisSession.add(item.id)
                Inertia.reload({ preserveScroll: true })
            }
        })
        .catch(() => {})
}

const tabActiveStyle = { backgroundColor: SIA_GOLD_ACTIVE_BG, color: SIA_GOLD_ACTIVE_TEXT, borderColor: SIA_GOLD_ACTIVE_BG }
const tabInactiveStyle = { border: '1px solid #e4e6ef', backgroundColor: '#fff', color: '#1e1e2d' }

function sendVideoProgress(item, video) {
    const duration = Math.floor(video?.duration || 0)
    const position = Math.floor(video?.currentTime || 0)
    if (!duration) return
    const ended = video.ended || position / duration >= 0.99
    if (ended || position / duration >= 0.2) {
        reportProgress(item, {
            event: ended ? 'ended' : 'tick',
            position_seconds: position,
            duration_seconds: duration,
        })
    }
}

function YoutubeFrame({ item, title }) {
    const frameId = `sia-yt-${item.id}`
    const src = `${getYouTubeEmbedSrc(item.youtube_url)}?enablejsapi=1&rel=0`

    React.useEffect(() => {
        let player
        let timer
        const bind = () => {
            if (!window.YT?.Player || player) return
            player = new window.YT.Player(frameId, {
                events: {
                    onStateChange: (event) => {
                        if (event.data === 0) {
                            const duration = Math.floor(player.getDuration?.() || 0)
                            reportProgress(item, {
                                event: 'ended',
                                position_seconds: duration,
                                duration_seconds: duration,
                            })
                        }
                    },
                },
            })
            timer = window.setInterval(() => {
                const duration = player?.getDuration?.() || 0
                const position = player?.getCurrentTime?.() || 0
                if (duration > 0 && position / duration >= 0.2) {
                    reportProgress(item, {
                        event: 'tick',
                        position_seconds: Math.floor(position),
                        duration_seconds: Math.floor(duration),
                    })
                }
            }, 5000)
        }

        if (window.YT?.Player) {
            bind()
        } else {
            const previous = window.onYouTubeIframeAPIReady
            window.onYouTubeIframeAPIReady = () => {
                if (typeof previous === 'function') previous()
                bind()
            }
            if (!document.getElementById('sia-yt-iframe-api')) {
                const tag = document.createElement('script')
                tag.id = 'sia-yt-iframe-api'
                tag.src = 'https://www.youtube.com/iframe_api'
                document.body.appendChild(tag)
            }
        }

        return () => window.clearInterval(timer)
    }, [item.id, frameId])

    return (
        <div className="ratio ratio-16x9 rounded overflow-hidden border">
            <iframe
                id={frameId}
                title={title || item?.title || 'Video'}
                src={src}
                className="border-0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
            />
        </div>
    )
}

function VideoPlayer({ item, title }) {
    if (item?.is_mp4 || item?.video_url) {
        if (!item.video_url) {
            return (
                <div className="ratio ratio-16x9 bg-light d-flex align-items-center justify-content-center border rounded">
                    <span className="text-muted">Video pendiente de publicar</span>
                </div>
            )
        }
        return (
            <div className="ratio ratio-16x9 rounded overflow-hidden border bg-dark">
                <video
                    src={item.video_url}
                    controls
                    playsInline
                    controlsList="nodownload"
                    disablePictureInPicture
                    onContextMenu={(e) => e.preventDefault()}
                    onTimeUpdate={(e) => sendVideoProgress(item, e.currentTarget)}
                    onEnded={(e) => sendVideoProgress(item, e.currentTarget)}
                    className="w-100 h-100"
                    style={{ objectFit: 'contain' }}
                >
                    Tu navegador no reproduce este video.
                </video>
            </div>
        )
    }

    const src = item?.youtube_url ? getYouTubeEmbedSrc(item.youtube_url) : null
    if (!src) {
        return (
            <div className="ratio ratio-16x9 bg-light d-flex align-items-center justify-content-center border rounded">
                <span className="text-muted">Video no disponible</span>
            </div>
        )
    }
    return <YoutubeFrame item={item} title={title} />
}

function DownloadFile({ item }) {
    const href = item.download_route || route('sia.download', item.id)

    return (
        <div className="border rounded p-6 bg-light">
            <p className="text-gray-800 mb-4">Este archivo es de Excel. Descárgalo para abrirlo.</p>
            <a href={href} className="btn btn-primary" onClick={() => reportProgress(item, { event: 'open' })}>
                Descargar Excel
            </a>
        </div>
    )
}

function PdfViewer({ item }) {
    React.useEffect(() => {
        if (item?.file_url) reportProgress(item, { event: 'open' })
    }, [item])

    if (!item?.file_url) {
        return (
            <div className="bg-light d-flex align-items-center justify-content-center border rounded" style={{ minHeight: 240 }}>
                <span className="text-muted">PDF pendiente de publicar</span>
            </div>
        )
    }

    return (
        <>
            <iframe title={item.title || 'PDF'} src={item.file_url} className="w-100 border rounded" style={{ height: '70vh' }} />
            <a href={item.download_route || route('sia.download', item.id)} className="btn btn-primary mt-4">
                Descargar PDF
            </a>
        </>
    )
}

function LockedContent() {
    return (
        <div className="p-8 text-muted">
            Este contenido está cerrado. Termina el anterior, en el orden de la capacitación, para abrirlo.
        </div>
    )
}

function MediaPane({ item }) {
    if (item?.locked) return <LockedContent />
    if (item?.type === 'document') {
        if (isSpreadsheet(item)) return <DownloadFile item={item} />
        return <PdfViewer item={item} />
    }
    return <VideoPlayer item={item} />
}

function ProductTabsLayout({ section }) {
    const items = section.items || []
    const tabsPresent = useMemo(() => {
        const set = new Set()
        items.forEach((i) => {
            if (i.tab) set.add(i.tab)
        })
        return TAB_ORDER.filter((t) => set.has(t))
    }, [items])

    const [activeTab, setActiveTab] = useState(() => tabsPresent[0] || 'producto')
    const tabItems = useMemo(() => items.filter((i) => i.tab === activeTab), [items, activeTab])

    if (!tabsPresent.length) {
        return <div className="p-8 text-muted">No hay contenido configurado para esta sección.</div>
    }

    return (
        <>
            <div className="border-bottom pb-4 mb-6">
                <Nav variant="pills" className="flex-wrap gap-2">
                    {tabsPresent.map((tab) => (
                        <Nav.Item key={tab}>
                            <button
                                type="button"
                                className="nav-link rounded px-4 py-2 fw-semibold"
                                style={activeTab === tab ? tabActiveStyle : tabInactiveStyle}
                                onClick={() => setActiveTab(tab)}
                            >
                                {TAB_LABELS[tab] || tab}
                            </button>
                        </Nav.Item>
                    ))}
                </Nav>
            </div>

            {activeTab === 'recursos' && (
                <p className="text-center text-gray-700 mb-6">Has clic en el enlace para descargar el recurso.</p>
            )}

            {activeTab !== 'recursos' && activeTab !== 'videos_adicionales' && (
                <div className="mb-4">
                    {tabItems.length === 1 ? (
                        <>
                            <MediaPane item={tabItems[0]} />
                            <LessonMeta item={tabItems[0]} />
                        </>
                    ) : (
                        tabItems.map((it) => (
                            <div key={it.id} className="mb-8">
                                <h4 className="fs-5 mb-3">{it.title}</h4>
                                <MediaPane item={it} />
                                <LessonMeta item={it} />
                            </div>
                        ))
                    )}
                </div>
            )}

            {activeTab === 'videos_adicionales' && (
                <Row className="g-6">
                    {tabItems.map((it) => (
                        <Col md={6} key={it.id}>
                            <Card className="h-100 border shadow-sm">
                                <Card.Header className="bg-light py-3 px-3 fw-semibold text-gray-900 border-bottom">{it.title}</Card.Header>
                                <Card.Body className="p-0">
                                    <MediaPane item={it} />
                                    <div className="p-4">
                                        <LessonMeta item={it} />
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>
                    ))}
                </Row>
            )}

            {activeTab === 'recursos' && (
                <Card className="border">
                    <Card.Body className="p-0">
                        {tabItems.length === 0 ? (
                            <div className="p-6 text-muted">No hay recursos publicados.</div>
                        ) : (
                            tabItems.map((it, idx) => (
                                <div
                                    key={it.id}
                                    className={`px-6 py-4 ${idx < tabItems.length - 1 ? 'border-bottom' : ''}`}
                                >
                                    <div className="d-flex justify-content-between align-items-center">
                                        <span className="fw-semibold text-gray-900">{it.title}</span>
                                        <a href={route('sia.download', it.id)} className="btn btn-sm btn-light-primary">
                                            Descargar
                                        </a>
                                    </div>
                                    <LessonMeta item={it} />
                                </div>
                            ))
                        )}
                    </Card.Body>
                </Card>
            )}
        </>
    )
}

function SidebarVideosLayout({ section }) {
    const items = section.items || []
    const [activeIdx, setActiveIdx] = useState(0)
    const current = items[activeIdx] || items[0]

    if (!items.length) {
        return <div className="p-8 text-muted">Aún no hay material en esta sección. Pronto se publicará el contenido.</div>
    }

    return (
        <Row className="g-0">
            <Col lg={4} className="align-self-lg-start border-bottom border-lg-end border-gray-200 bg-white rounded-start-lg" style={mediaListStyle}>
                <div className="list-group list-group-flush rounded-0">
                    {items.map((it, idx) => {
                        const selected = idx === activeIdx
                        return (
                            <button
                                key={it.id}
                                type="button"
                                className="list-group-item list-group-item-action border-0 text-start py-4 px-5 fw-semibold"
                                style={
                                    selected
                                        ? {
                                              backgroundColor: SIA_GOLD_ACTIVE_BG,
                                              color: SIA_GOLD_ACTIVE_TEXT,
                                              borderLeft: `4px solid ${SIA_GOLD_ACTIVE_TEXT}`,
                                          }
                                        : { backgroundColor: '#fff', color: '#3f4254' }
                                }
                                onClick={() => setActiveIdx(idx)}
                            >
                                {it.title}
                                {it.locked ? <span className="fw-normal"> · Cerrado</span> : null}
                                {it.completed ? <span className="fw-normal"> · Visto</span> : null}
                                {it.type === 'document' ? <span className="text-muted fw-normal"> · {documentKindLabel(it)}</span> : null}
                            </button>
                        )
                    })}
                </div>
            </Col>
            <Col lg={8} className="bg-white p-6 p-lg-8 rounded-end-lg">
                {current ? (
                    <>
                        <h3 className="fs-4 mb-6 text-gray-900">{current.title}</h3>
                        <MediaPane item={current} />
                        <LessonMeta item={current} />
                    </>
                ) : (
                    <div className="text-muted">No hay material en esta sección.</div>
                )}
            </Col>
        </Row>
    )
}

function VideoGridLayout({ section }) {
    const items = section.items || []
    if (!items.length) {
        return <div className="p-8 text-muted">Aún no hay material en esta sección.</div>
    }
    return (
        <Row className="g-6 p-4">
            {items.map((it) => (
                <Col md={6} key={it.id}>
                    <Card className="h-100 border shadow-sm">
                        <Card.Header className="bg-light py-3 px-3 fw-semibold text-gray-900 border-bottom">{it.title}</Card.Header>
                        <Card.Body className={it.type === 'document' ? 'p-4' : 'p-0'}>
                            <MediaPane item={it} />
                            <div className="p-4">
                                <LessonMeta item={it} />
                            </div>
                        </Card.Body>
                    </Card>
                </Col>
            ))}
        </Row>
    )
}

function linesFromText(text) {
    if (!text) return []
    return String(text)
        .split(/\r?\n/)
        .map((line) => line.trim())
        .filter(Boolean)
}

function LessonMeta({ item }) {
    if (!item) return null
    const highlights = linesFromText(item.highlights)
    const applications = linesFromText(item.application_text)
    const hasMeta =
        item.author ||
        item.duration_label ||
        item.main_topic ||
        highlights.length > 0 ||
        applications.length > 0 ||
        item.description

    if (!hasMeta) return null

    return (
        <div className="mt-8 pt-6 border-top">
            <div className="row g-6 mb-6">
                {item.author && (
                    <div className="col-md-6">
                        <div className="text-muted fs-7 text-uppercase fw-semibold mb-1">Autor</div>
                        <div className="fs-5 text-gray-900">{item.author}</div>
                    </div>
                )}
                {item.duration_label && (
                    <div className="col-md-6">
                        <div className="text-muted fs-7 text-uppercase fw-semibold mb-1">Tiempo</div>
                        <div className="fs-5 text-gray-900">{item.duration_label}</div>
                    </div>
                )}
            </div>
            {item.main_topic && (
                <div className="mb-6">
                    <div className="text-muted fs-7 text-uppercase fw-semibold mb-2">Tema principal</div>
                    <p className="fs-5 text-gray-800 mb-0 lh-lg">{item.main_topic}</p>
                </div>
            )}
            {highlights.length > 0 && (
                <div className="mb-6">
                    <div className="text-muted fs-7 text-uppercase fw-semibold mb-2">Puntos más importantes</div>
                    <ul className="fs-5 text-gray-800 ps-5 mb-0">
                        {highlights.map((line, idx) => (
                            <li key={idx} className="mb-2">
                                {line}
                            </li>
                        ))}
                    </ul>
                </div>
            )}
            {applications.length > 0 && (
                <div className="mb-6">
                    <div className="text-muted fs-7 text-uppercase fw-semibold mb-2">Aplicación del asesor</div>
                    <ul className="fs-5 text-gray-800 ps-5 mb-0">
                        {applications.map((line, idx) => (
                            <li key={idx} className="mb-2">
                                {line}
                            </li>
                        ))}
                    </ul>
                </div>
            )}
            {item.description && (
                <div>
                    <div className="text-muted fs-7 text-uppercase fw-semibold mb-2">Descripción</div>
                    <p className="fs-5 text-gray-800 mb-0 lh-lg">{item.description}</p>
                </div>
            )}
        </div>
    )
}

function TopicCard({ title, text, badge, href }) {
    return (
        <Col md={6}>
            <Card className="h-100 shadow-sm border border-gray-200">
                <Card.Body className="d-flex flex-column">
                    <Card.Title className="fs-5 text-gray-900">{title}</Card.Title>
                    <Card.Text className="text-gray-600 flex-grow-1">{text}</Card.Text>
                    <div className="d-flex justify-content-between align-items-center mt-3">
                        <span className="badge badge-light-primary">{badge}</span>
                        <Link href={href} className="btn btn-sm btn-primary">
                            Abrir
                        </Link>
                    </div>
                </Card.Body>
            </Card>
        </Col>
    )
}

function SectionHubLayout({ section }) {
    const children = section.children || []
    if (!children.length) {
        return <div className="p-8 text-muted">No hay subtemas publicados en esta ruta.</div>
    }
    return (
        <Row className="g-5 p-4 p-lg-6">
            {children.map((child) => (
                <TopicCard
                    key={child.id}
                    title={child.title}
                    text={child.description || 'Abrir para ver el contenido de este subtema.'}
                    badge={
                        (child.children_count || 0) > 0
                            ? `${child.children_count} temas`
                            : `${child.items_count || 0} contenidos`
                    }
                    href={route('sia.section.show', child.slug)}
                />
            ))}
        </Row>
    )
}

function ExamListLink({ exam }) {
    return (
        <Link
            href={exam.href}
            className="list-group-item list-group-item-action border-0 text-start py-4 px-5 fw-semibold"
            style={{ backgroundColor: '#fff', color: '#8a7014' }}
        >
            Examen
            {exam.locked ? <span className="fw-normal"> · Falta ver el contenido</span> : null}
            {exam.status === 'in_progress' ? <span className="fw-normal"> · En curso</span> : null}
            {exam.score !== null && exam.score !== undefined ? (
                <span className="fw-normal"> · {exam.score}</span>
            ) : null}
        </Link>
    )
}

function LessonListLayout({ section, exam }) {
    const items = section.items || []
    const [activeIdx, setActiveIdx] = useState(0)
    const current = items[activeIdx] || items[0]

    if (!items.length) {
        return (
            <div className="p-8">
                <div className="text-muted mb-4">Aún no hay contenido en este subtema. El material se publicará pronto.</div>
                {exam && (
                    <div className="list-group list-group-flush">
                        <ExamListLink exam={exam} />
                    </div>
                )}
            </div>
        )
    }

    return (
        <Row className="g-0">
            <Col lg={4} className="align-self-lg-start border-bottom border-lg-end border-gray-200 bg-white rounded-start-lg" style={mediaListStyle}>
                <div className="list-group list-group-flush rounded-0">
                    {items.map((it, idx) => {
                        const selected = idx === activeIdx
                        return (
                            <button
                                key={it.id}
                                type="button"
                                className="list-group-item list-group-item-action border-0 text-start py-4 px-5 fw-semibold"
                                style={
                                    selected
                                        ? {
                                              backgroundColor: SIA_GOLD_ACTIVE_BG,
                                              color: SIA_GOLD_ACTIVE_TEXT,
                                              borderLeft: `4px solid ${SIA_GOLD_ACTIVE_TEXT}`,
                                          }
                                        : { backgroundColor: '#fff', color: '#3f4254' }
                                }
                                onClick={() => setActiveIdx(idx)}
                            >
                                {it.title}
                                {it.locked ? <span className="fw-normal"> · Cerrado</span> : null}
                                {it.completed ? <span className="fw-normal"> · Visto</span> : null}
                                {it.type === 'document' ? <span className="fw-normal"> · {documentKindLabel(it)}</span> : null}
                            </button>
                        )
                    })}
                    {exam && <ExamListLink exam={exam} />}
                </div>
            </Col>
            <Col lg={8} className="bg-white p-6 p-lg-8 rounded-end-lg">
                {current ? (
                    <>
                        <h3 className="fs-4 mb-6 text-gray-900">{current.title}</h3>
                        <MediaPane item={current} />
                        <LessonMeta item={current} />
                    </>
                ) : (
                    <div className="text-muted">No hay material en este subtema.</div>
                )}
            </Col>
        </Row>
    )
}

const SectionShow = ({ section, ancestors = [], exam = null }) => {
    const { sia_use_learner_layout } = usePage().props
    const banner = section.banner_title || section.title
    const path = `/admin/sia/${section.slug}`
    const layoutType = section.layout_type
    const filesDirectly = layoutType === 'section_hub' && !(section.children || []).length
    const showFileList = layoutType === 'lesson_list' || filesDirectly || (layoutType === 'section_hub' && (section.items || []).length > 0)

    return (
        <>
            {!sia_use_learner_layout && (
                <div className="mb-5 mt-5">
                    <Breadcrumbs currentPath={path} />
                </div>
            )}

            <div className="d-flex flex-wrap align-items-center gap-2 mb-6">
                <Link href={route('sia.index')} className="btn btn-sm btn-light">
                    ← Volver al inicio SIA
                </Link>
                {ancestors.map((a) => (
                    <Link key={a.slug} href={route('sia.section.show', a.slug)} className="btn btn-sm btn-light-primary">
                        {a.title}
                    </Link>
                ))}
            </div>

            <div
                className="rounded-2 mb-8 py-5 px-4 text-center text-white text-uppercase fw-bold fs-3"
                style={{ backgroundColor: SIA_NAVY, letterSpacing: '0.02em' }}
            >
                {banner}
            </div>

            <div className="card card-flush shadow-sm mb-10">
                <div className="card-body p-0 p-lg-2">
                    {layoutType === 'product_tabs' && <ProductTabsLayout section={section} />}
                    {(layoutType === 'sidebar_videos' || layoutType === 'media_catalog') && (
                        <SidebarVideosLayout section={section} />
                    )}
                    {layoutType === 'video_grid' && <VideoGridLayout section={section} />}
                    {layoutType === 'section_hub' && !filesDirectly && <SectionHubLayout section={section} />}
                    {showFileList && <LessonListLayout section={section} exam={exam} />}
                </div>
            </div>

            {exam && (
                <div className="card card-flush shadow-sm mb-8">
                    <div className="card-body p-6 p-lg-8 d-flex flex-wrap justify-content-between align-items-center gap-4">
                        <div>
                            <div className="text-uppercase fw-semibold fs-7 mb-1" style={{ color: '#8a7014', letterSpacing: '0.06em' }}>
                                Examen
                            </div>
                            <div className="fw-bold fs-4 text-gray-900">{exam.title}</div>
                            {exam.locked && <p className="text-gray-600 mb-0 mt-2">{exam.lock_reason}</p>}
                            {exam.status === 'in_progress' && <p className="text-gray-600 mb-0 mt-2">Tienes un examen en curso.</p>}
                            {exam.score !== null && exam.score !== undefined && (
                                <p className="mb-0 mt-2 fw-semibold" style={{ color: exam.passed ? '#0f7a3a' : '#9b2c2c' }}>
                                    Calificación {exam.score} · {exam.passed ? 'Aprobado' : 'No aprobado'}
                                </p>
                            )}
                        </div>
                        {!exam.locked && (
                            <Link
                                href={exam.href}
                                className="btn fw-semibold"
                                style={{ backgroundColor: SIA_GOLD_ACTIVE_BG, color: SIA_GOLD_ACTIVE_TEXT, border: 'none' }}
                            >
                                {exam.status === 'passed' || exam.status === 'failed' ? 'Ver resultado' : exam.status === 'in_progress' ? 'Continuar examen' : 'Presentar examen'}
                            </Link>
                        )}
                    </div>
                </div>
            )}

            <p className="text-center text-muted small mb-10">
                Copyright © EACM {new Date().getFullYear()} Todos los derechos reservados. SIA
            </p>
        </>
    )
}

SectionShow.layout = (page) => {
    const title = page.props.section?.title || 'SIA'
    if (page.props.sia_use_learner_layout) {
        return <LayoutSiaLearner title={`${title} | SIA`}>{page}</LayoutSiaLearner>
    }
    return <Layout title={title}>{page}</Layout>
}

export default SectionShow
